/**
 * LoanTabShell — the page frame for a Loan Drilldown tab.
 *
 * Owns the two things the Drilldown Tab Header Standard fixes so no tab has to
 * decide them:
 *   1. the canvas — `LoanTabCanvas`, the root wrapper that sets the gap from the
 *      tab switcher to the content (the Properties/Applicants inset), and
 *   2. the rhythm — 18px from the page header to whatever comes next, 14px
 *      between peer cards.
 *
 * The 18px gap does NOT change based on what follows (KPI strip or card). That
 * constancy is the entire point: it's what keeps the title landing in the same
 * place when you switch between tabs with different content shapes.
 *
 * Standard structure for a new tab:
 *
 *   <LoanTabShell
 *     title="Closed package"
 *     subTitle="One line."
 *     primaryAction={{ label: "Add item", onClick }}
 *     kpis={[…4–6 tiles, or omit…]}
 *   >
 *     <LoanSectionCard title="First section">…</LoanSectionCard>
 *     <LoanSectionCard title="Second section">…</LoanSectionCard>
 *   </LoanTabShell>
 *
 * Children are peer cards. Don't nest a card inside a card — flatten it.
 */
import PropTypes from "prop-types";
import { memo, useMemo } from "react";

import Flx from "../../../../components/layout/Flx";
import LoanTabCanvas from "./LoanTabCanvas";
import LoanTabHeader from "./LoanTabHeader";
import { LOAN_TAB_MAX_WIDTH } from "./loanTabLayoutTokens";
import { TAB_TOKENS as T } from "./tabTokens";

const LoanTabShell = ({
  children,
  // Canvas
  maxWidth = LOAN_TAB_MAX_WIDTH,
  sx,
  bodySx,
  /** Rendered above the header — banners, trade strips, alerts. */
  startContent,
  // Header slots — forwarded to LoanTabHeader.
  title,
  subTitle,
  subTitleContent,
  eyebrow,
  meta,
  primaryAction,
  secondaryActions,
  endContent,
  kpis,
  bottomContent,
  stickyHeader,
  autoFocusTitle,
  titleId,
  headerSx,
}) => {
  const hasHeader = Boolean(title || kpis?.length || bottomContent);

  const bodyStyles = useMemo(
    () => ({
      gap: T.space.cardGap,
      ...(hasHeader && { marginTop: T.space.headerGap }),
      ...bodySx,
    }),
    [hasHeader, bodySx],
  );

  return (
    <LoanTabCanvas maxWidth={maxWidth} sx={sx}>
      {startContent}
      <LoanTabHeader
        title={title}
        subTitle={subTitle}
        subTitleContent={subTitleContent}
        eyebrow={eyebrow}
        meta={meta}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        endContent={endContent}
        kpis={kpis}
        bottomContent={bottomContent}
        sticky={stickyHeader}
        autoFocusTitle={autoFocusTitle}
        titleId={titleId}
        sx={headerSx}
      />
      <Flx column sx={bodyStyles}>
        {children}
      </Flx>
    </LoanTabCanvas>
  );
};

LoanTabShell.propTypes = {
  /** Peer cards — normally LoanSectionCard elements. */
  children: PropTypes.node,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  sx: PropTypes.object,
  bodySx: PropTypes.object,
  startContent: PropTypes.node,
  title: PropTypes.node,
  subTitle: PropTypes.node,
  /** Small supporting content (a legend, a key) directly under the subtitle. */
  subTitleContent: PropTypes.node,
  eyebrow: PropTypes.node,
  meta: PropTypes.node,
  primaryAction: PropTypes.oneOfType([PropTypes.object, PropTypes.element]),
  secondaryActions: PropTypes.array,
  endContent: PropTypes.node,
  kpis: PropTypes.array,
  bottomContent: PropTypes.node,
  stickyHeader: PropTypes.bool,
  autoFocusTitle: PropTypes.bool,
  titleId: PropTypes.string,
  headerSx: PropTypes.object,
};

export default memo(LoanTabShell);
